import { motion } from "framer-motion";
import { Linkedin } from "lucide-react";
import CountdownTimer from "./CountdownTimer";

export default function HeroSection() {
  return (
    <section
      data-testid="hero-section"
      className="relative h-screen flex flex-col items-center justify-center px-6 md:px-12 overflow-hidden"
      style={{ perspective: "1200px" }}
    >
      {/* Perspective grid floor */}
      <div className="perspective-grid" />

      {/* 3D Orbital rings */}
      <div className="orbital-container">
        <div className="orbital-ring" style={{ transform: "rotateX(70deg) rotateZ(0deg)" }}>
          <div className="orbital-dot" />
        </div>
        <div className="orbital-ring orbital-ring-2" />
        <div className="orbital-ring orbital-ring-3" />
      </div>

      {/* Floating geometric shapes */}
      <div className="geo-shape" style={{ top: "15%", left: "8%", width: "80px", height: "80px", border: "1px solid rgba(0,229,255,0.1)", borderRadius: "12px", transform: "rotate(45deg)", animation: "float-1 8s ease-in-out infinite" }} />
      <div className="geo-shape" style={{ top: "20%", right: "10%", width: "60px", height: "60px", border: "1px solid rgba(123,94,167,0.1)", borderRadius: "50%", animation: "float-2 10s ease-in-out infinite 1s" }} />
      <div className="geo-shape" style={{ bottom: "25%", left: "12%", width: "40px", height: "40px", border: "1px solid rgba(245,166,35,0.08)", borderRadius: "8px", animation: "float-3 12s ease-in-out infinite 2s" }} />
      <div className="geo-shape" style={{ bottom: "30%", right: "8%", width: "70px", height: "70px", border: "1px solid rgba(0,229,255,0.06)", borderRadius: "50%", animation: "float-1 14s ease-in-out infinite 0.5s" }} />
      <div className="geo-shape" style={{ top: "40%", left: "5%", width: "50px", height: "50px", border: "1px solid rgba(107,127,215,0.08)", clipPath: "polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)", animation: "float-2 9s ease-in-out infinite 3s" }} />

      {/* Central glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-[55%] w-[400px] h-[400px] md:w-[700px] md:h-[700px] rounded-full pointer-events-none z-[2]"
        style={{ background: "radial-gradient(circle, rgba(0,229,255,0.06) 0%, rgba(123,94,167,0.03) 30%, transparent 60%)", filter: "blur(40px)" }}
      />

      {/* Logo */}
      <motion.div
        initial={{ opacity: 0, y: -60, scale: 0.6, rotateX: 30 }}
        animate={{ opacity: 1, y: 0, scale: 1, rotateX: 0 }}
        transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
        className="mb-5 md:mb-6 relative z-20"
      >
        <motion.img
          data-testid="logo"
          src="/logo.png"
          alt="Eminal Dynamics"
          className="h-auto w-[160px] sm:w-[200px] md:w-[260px] object-contain"
          style={{ filter: "drop-shadow(0 0 50px rgba(0,229,255,0.2))" }}
          animate={{ y: [0, -10, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        />
      </motion.div>

      {/* Headline */}
      <div className="text-center max-w-5xl mx-auto mb-4 md:mb-5 relative z-20" style={{ perspective: "1200px" }}>
        <motion.h1
          initial={{ opacity: 0, y: 60, rotateX: 25 }}
          animate={{ opacity: 1, y: 0, rotateX: 0 }}
          transition={{ duration: 1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="text-4xl sm:text-5xl md:text-7xl lg:text-[5.5rem] font-bold tracking-tight leading-[1.05] text-white"
        >
          El futuro de la empresa
        </motion.h1>
        <motion.h1
          initial={{ opacity: 0, y: 60, rotateX: 25 }}
          animate={{ opacity: 1, y: 0, rotateX: 0 }}
          transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="text-4xl sm:text-5xl md:text-7xl lg:text-[5.5rem] font-bold tracking-tight leading-[1.05] mt-1"
        >
          <motion.span
            data-testid="gradient-headline"
            className="inline-block bg-gradient-to-r from-[#00E5FF] via-[#4DA8FF] to-[#7B5EA7] bg-clip-text text-transparent text-glow-cyan"
            animate={{ backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"] }}
            transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
            style={{ backgroundSize: "200% 100%" }}
          >
            ya tiene nombre.
          </motion.span>
        </motion.h1>
      </div>

      {/* Subheadline */}
      <motion.p
        data-testid="subheadline"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.7 }}
        className="text-sm sm:text-base md:text-lg text-[#6B7FD7] max-w-xl text-center leading-relaxed mb-5 md:mb-6 relative z-20 mx-auto"
      >
        Vamos a automatizar el futuro de la PYME española. Más revenue. Menos costes.
        Cero complicaciones. Soluciones a medida para todos los sectores.
      </motion.p>

      {/* Countdown */}
      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.8, delay: 0.9 }}
        className="mb-5 md:mb-6 relative z-20"
      >
        <CountdownTimer targetDate="2026-09-11T00:00:00" />
      </motion.div>

      {/* LinkedIn CTA */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1.1 }}
        className="relative z-20"
      >
        <div className="absolute inset-0 rounded-full border border-[#F5A623]/20" style={{ animation: "pulse-ring 2.5s ease-out infinite" }} />
        <div className="absolute inset-0 rounded-full border border-[#F5A623]/15" style={{ animation: "pulse-ring 2.5s ease-out infinite 0.6s" }} />
        <motion.a
          data-testid="linkedin-cta"
          href="https://www.linkedin.com/company/eminal-dynamics"
          target="_blank"
          rel="noopener noreferrer"
          whileHover={{ scale: 1.06, boxShadow: "0 0 50px rgba(245,166,35,0.35)" }}
          whileTap={{ scale: 0.97 }}
          className="relative inline-flex items-center gap-3 px-8 py-3.5 rounded-full border border-[#F5A623]/50 bg-[#F5A623]/[0.08] text-[#F5A623] hover:bg-[#F5A623]/[0.15] hover:border-[#F5A623]/70 transition-all duration-300 animate-glow-pulse"
        >
          <Linkedin className="w-5 h-5" />
          <span className="text-sm font-semibold tracking-wide uppercase">Síguenos en LinkedIn</span>
        </motion.a>
      </motion.div>
    </section>
  );
}
